import classNames from "classnames";
import dayjs from "dayjs";
import { get, map, maxBy } from "lodash";
import { useTranslation } from "react-i18next";
import { MdOpenInNew, MdOutlineEventBusy } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Button from "shared/components/Button";
import Select from "shared/components/Select";
import Status from "shared/components/Status";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { USERS } from "utils/api/graphql/queries/users";
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  PROCESS_TAGS as PROCESS_TAGS_STATUSES,
  SCREENS,
  THEMES_PALETTE,
  USER_TYPES,
} from "utils/constants";

const Projects = ({ projects, translate, isAssigning, onAssign, tracers }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const navigate = useNavigate();
  const PROCESS_TAGS = t("PROCESS_TAGS", { returnObjects: true });

  const matchStatusesWithProcess = (statuses) => {
    const result = map(
      statuses.filter(({ status }) => status),
      ({ status: { id: statusId, order } }) => {
        const allProcessStatuses = tracers.flatMap(
          (tracer) =>
            tracer?.flow?.process?.processStatuses
              ?.filter(({ id }) => id === statusId)
              .map((status) => ({
                ...status,
                tracer,
              })) || [],
        );
        const matchedStatus = maxBy(allProcessStatuses, (candidate) => {
          return -Math.abs(candidate.order - order);
        });

        return {
          tracer: matchedStatus?.tracer || null,
          processStatus: matchedStatus || null,
        };
      },
    ).filter(({ tracer }) => tracer !== null);
    return maxBy(result, ({ processStatus: { order } }) => parseInt(order, 10));
  };

  const projectStatus = (project) => {
    const statusData = matchStatusesWithProcess(get(project, "statuses"));
    const statusText =
      (project.expired && PROCESS_TAGS.EXPIRED) ||
      get(statusData, "processStatus.name", PROCESS_TAGS.IN_PROGRESS);
    return (
      <Status
        type="custom"
        style={{
          color: get(statusData, "processStatus.color"),
          background: get(statusData, "processStatus.background"),
        }}
        text={statusText}
      />
    );
  };

  const hasNoStatus = (project) => {
    const statusData = matchStatusesWithProcess(get(project, "statuses"));
    return !get(statusData, "tracer");
  };

  const handleProjectClick = (project) => {
    const statusData = matchStatusesWithProcess(get(project, "statuses"));
    if (!get(statusData, "tracer")) return;
    navigate(`${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${project.id}`, {
      state: {
        process: get(statusData, "tracer.flow.process"),
        tracer: get(statusData, "tracer"),
      },
    });
  };
  return (
    <div className="projects__wrapper">
      {get(projects, "data", []).map((project, i) => (
        <div className="single-project" key={`project-${i}`}>
          <div className="single-project__detail">
            <div className="single-project__detail--header">
              <div className="left">
                {projectStatus(project)}
                <span className="product_detail--createdDate">
                  {translate.CREATED_AT}
                  {dayjs(project.createdDate).format(DATE_TIME_FORMAT)}
                </span>
              </div>
              <div className="right">
                <Status
                  type={"simple"}
                  text={get(
                    get(
                      THEMES_PALETTE,
                      project?.fields?.processTemplate,
                      "DEFAULT",
                    ),
                    "NAME",
                  )}
                  status={"info"}
                />
              </div>
            </div>
          </div>
          <div className="single-project__author">
            <div
              className={classNames("single-project__author--effectiveDate", {
                expired: project.expired,
              })}
            >
              <MdOutlineEventBusy
                size={16}
                color={project.expired ? COLORS.C_DANGER : COLORS.C_GRAY}
              />
              {dayjs(project.contract.issuanceDate).format(DATE_FORMAT)}
            </div>
            <div className="single-project__author--name">
              {project.author?.id ? (
                <>
                  <label>{translate.ATTRIBUTED_TO}</label>
                  <span className="value">
                    {project.author?.firstname} {project.author?.lastname}
                  </span>
                </>
              ) : (
                <Select
                  type="async"
                  node={{
                    query: USERS,
                    variables: {
                      where: {
                        AND: [
                          { isActive: true },
                          {
                            OR: [
                              {
                                meta: {
                                  type: USER_TYPES.INTERNAL_ADVISOR.type,
                                },
                              },
                              {
                                meta: {
                                  type: USER_TYPES.INTERNAL_SUPERVISOR.type,
                                },
                              },
                              {
                                meta: { type: USER_TYPES.BROKER_EMPLOYEE.type },
                              },
                              { meta: { type: USER_TYPES.BROKER_ADMIN.type } },
                              {
                                meta: {
                                  type: USER_TYPES.BROKER_REPRESENTATIVE.type,
                                },
                              },
                            ],
                          },
                        ],
                      },
                    },
                    accessors: {
                      root: "users.data",
                      label: {
                        separator: " ",
                        keys: ["firstname", "lastname"],
                      },
                    },
                  }}
                  placeholder={translate.BROKER_FILTER_PLACEHOLDER}
                  onChange={(value) =>
                    onAssign({ projectId: project.id, authorId: value })
                  }
                  disabled={
                    isAssigning[project.author?.id] ||
                    [PROCESS_TAGS_STATUSES.SIGNED].includes(
                      get(
                        matchStatusesWithProcess(get(project, "statuses")),
                        "processStatus.tag",
                      ),
                    )
                  }
                  loading={isAssigning[project.author?.id]}
                />
              )}
            </div>
          </div>
          <Button
            className="single-project__link"
            type="primary--link"
            onClick={() => handleProjectClick(project)}
            disabled={hasNoStatus(project)}
          >
            <span>Ouvrir le projet</span>
            <MdOpenInNew size={20} />
          </Button>
        </div>
      ))}
    </div>
  );
};
export default Projects;
